import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { SparkIcon, TooltipTrigger } from '@spark';
import { generateInitialParserCode } from '../components/parserCodeGenerator';
import { getValuesByPath } from '../components/utils';
import { TaskType, isGenerativeTask } from '@/shared/domain/taskTypes';
import { isParseableJsonStructure, parseJsonLenient } from '@/shared/utils/helpers';

const normalizeFieldPath = (path) => {
    if (!path) return '';
    return path.replace(/\[\d+\]/g, '').replace(/^\./, '').replace(/\.$/, '');
};

const formatPrimitive = (value) => {
    if (typeof value === 'string') {
        return `"${value}"`;
    }
    if (value === null) return 'null';
    return String(value);
};

const getEmptyNerAssignments = () => ({
    label: null,
    content: null,
    startIndex: null,
    relationLabel: null,
    relationSourceIndex: null,
    relationTargetIndex: null,
});

const DELIMITER_OPTIONS = [
    { label: 'List', value: 'list' },
    { label: ',', value: ',' },
    { label: '|', value: '|' },
    { label: ';', value: ';' },
    { label: ':', value: ':' },
    { label: '<space>', value: ' ' },
];

/**
 * Response-parser mapping state, extracted from ResponseInterpreterSection so
 * it can be reused outside the Model Configuration tab (e.g. the Playground
 * "Inspect" modal). Owns: raw-response JSON parsing, the JSON token/field
 * selection, NER field assignments, classification/summarization label field
 * + delimiter, parser prefill notification, and auto parser-code generation.
 *
 * Returns the full prop bundle `ModelOutputStep` consumes plus
 * `handleResponseTypeChange` / `showHighlightInstruction` for the host.
 */
export const useResponseMappingState = ({
    taskType,
    isMultilabel,
    responseType,
    setResponseType,
    rawResponse,
    initialParserSelections = {},
    onParserPrefillChange,
    onUserCodeChange,
}) => {
    const notifyParserPrefill = useCallback((data) => {
        if (typeof onParserPrefillChange === 'function') {
            onParserPrefillChange(data);
        }
    }, [onParserPrefillChange]);

    const { parsedJson, jsonParseError } = useMemo(() => {
        if (responseType !== 'json') {
            return { parsedJson: null, jsonParseError: null };
        }
        if (!rawResponse || !rawResponse.trim()) {
            return { parsedJson: null, jsonParseError: null };
        }
        try {
            const result = parseJsonLenient(rawResponse);
            return { parsedJson: result, jsonParseError: null };
        } catch (error) {
            return { parsedJson: null, jsonParseError: error.message || 'Failed to parse JSON' };
        }
    }, [rawResponse, responseType]);

    const [selectedJsonTokens, setSelectedJsonTokens] = useState({});
    const [selectedJsonLabelField, setSelectedJsonLabelField] = useState(null);
    // Single-label classification with a list-valued field (e.g. Jev's
    // {"labels": ["harmful"]}): whether the label is the list's first item
    // ('first', default) or the stringified list ('full'). Only consulted
    // when the selected field's sample value is actually an array.
    const [singleLabelListMode, setSingleLabelListMode] = useState('first');

    const supportsRelationFields = taskType === TaskType.NER_WITH_RELATIONS;

    const entityFieldConfig = useMemo(() => ([
        { key: 'label', label: 'Label' },
        { key: 'content', label: 'Content' },
        { key: 'startIndex', label: 'Start index' },
    ]), []);
    const relationFieldConfig = useMemo(() => ([
        { key: 'relationLabel', label: 'Label' },
        { key: 'relationSourceIndex', label: 'Source index' },
        { key: 'relationTargetIndex', label: 'Target index' },
    ]), []);

    const nerFieldConfig = useMemo(
        () => (supportsRelationFields ? [...entityFieldConfig, ...relationFieldConfig] : entityFieldConfig),
        [supportsRelationFields, entityFieldConfig, relationFieldConfig]
    );

    const nerFieldLabelMap = useMemo(() => (
        nerFieldConfig.reduce((acc, { key, label }) => {
            acc[key] = label;
            return acc;
        }, {})
    ), [nerFieldConfig]);

    const [nerFieldAssignments, setNerFieldAssignments] = useState(getEmptyNerAssignments);
    const isNerJsonMode = (taskType === TaskType.NER || taskType === TaskType.NER_WITH_RELATIONS) && responseType === 'json';

    const nerFieldMenuItems = useMemo(() => {
        const renderLabel = (label, key) => {
            const isAssigned = !!nerFieldAssignments[key];
            if (!isAssigned) return label;

            return (
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                    <span>{label}</span>
                    <TooltipTrigger text="Replace existing">
                        <span style={{ color: '#8c8c8c', marginLeft: 8, display: 'inline-flex' }}>
                            <SparkIcon slug="refresh" size="small" />
                        </span>
                    </TooltipTrigger>
                </div>
            );
        };

        const items = [
            {
                key: 'entity_group',
                type: 'group',
                label: 'Entity',
                children: entityFieldConfig.map(({ key, label }) => ({ key, label: renderLabel(label, key) })),
            },
        ];
        if (supportsRelationFields) {
            items.push(
                { type: 'divider' },
                {
                    key: 'relation_group',
                    type: 'group',
                    label: 'Relations',
                    children: relationFieldConfig.map(({ key, label }) => ({ key, label: renderLabel(label, key) })),
                },
            );
        }
        return items;
    }, [entityFieldConfig, relationFieldConfig, supportsRelationFields, nerFieldAssignments]);

    const nerPathAssignments = useMemo(() => {
        if (!isNerJsonMode) {
            return {};
        }
        const map = {};
        nerFieldConfig.forEach(({ key }) => {
            const assignment = nerFieldAssignments[key];
            if (assignment?.path) {
                const normalizedPath = normalizeFieldPath(assignment.path);
                const record = {
                    ...assignment,
                    fieldKey: key,
                    fieldLabel: nerFieldLabelMap[key],
                    normalizedPath,
                };
                map[normalizedPath] = record;
                map[`root.${normalizedPath}`] = record;
            }
        });
        return map;
    }, [isNerJsonMode, nerFieldAssignments, nerFieldConfig, nerFieldLabelMap]);

    const [multilabelDelimiter, setMultilabelDelimiter] = useState(',');
    const delimiterOptions = DELIMITER_OPTIONS;

    const jsonLabelFieldOptions = useMemo(() => {
        if (!parsedJson || typeof parsedJson !== 'object') {
            return [];
        }
        const optionsMap = new Map();
        const traverse = (value, path, arrayPath) => {
            if (!value || typeof value !== 'object') {
                return;
            }
            if (Array.isArray(value)) {
                value.forEach((item) => traverse(item, path, path || arrayPath || ''));
                return;
            }
            Object.entries(value).forEach(([key, child]) => {
                const fieldPath = path ? `${path}.${key}` : key;
                if (!optionsMap.has(fieldPath)) {
                    optionsMap.set(fieldPath, {
                        label: key,
                        value: fieldPath,
                        arrayPath: arrayPath ?? null,
                    });
                }
                traverse(child, fieldPath, arrayPath);
            });
        };
        traverse(parsedJson, '', '');
        return Array.from(optionsMap.values());
    }, [parsedJson]);

    const jsonFieldMetaMap = useMemo(() => {
        const map = {};
        jsonLabelFieldOptions.forEach((option) => {
            map[option.value] = option;
        });
        return map;
    }, [jsonLabelFieldOptions]);

    const jsonFieldSelectOptions = useMemo(() => (
        jsonLabelFieldOptions.map(({ value, label }) => ({
            value,
            label: value || label,
        }))
    ), [jsonLabelFieldOptions]);

    const normalizedJsonLabelField = useMemo(() => {
        if (selectedJsonLabelField) return selectedJsonLabelField;
        // Content generation: no selection means "entire response" — never
        // fall back to the first JSON field.
        if (taskType === TaskType.contentGeneration) return null;
        if (!jsonLabelFieldOptions.length) return null;
        return jsonLabelFieldOptions[0].value;
    }, [jsonLabelFieldOptions, selectedJsonLabelField, taskType]);

    const formatNerAssignedValue = (value) => {
        if (value === null || value === undefined) {
            return '';
        }
        if (typeof value === 'object') {
            try {
                return JSON.stringify(value);
            } catch {
                return String(value);
            }
        }
        return String(value);
    };

    const getFirstValueForPath = (value, path) => {
        const values = getValuesByPath(value, path);
        return values.length ? values[0] : '';
    };

    useEffect(() => {
        if (taskType === TaskType.classification && isMultilabel) {
            let textToAnalyze = '';
            if (responseType === 'text' && rawResponse) {
                textToAnalyze = rawResponse;
            } else if (responseType === 'json' && parsedJson && normalizedJsonLabelField) {
                const labelValues = getValuesByPath(parsedJson, normalizedJsonLabelField);
                if (labelValues && labelValues.length > 0) {
                    textToAnalyze = formatNerAssignedValue(labelValues[0]);
                }
            }

            if (!textToAnalyze) return;

            // If we have an explicit initial delimiter, skip auto-detection
            if (initialParserSelections?.classification?.delimiter) {
                return;
            }

            // Priority 1: List
            try {
                const cleaned = textToAnalyze.replace(/'/g, '"');
                const parsed = JSON.parse(cleaned);
                if (Array.isArray(parsed) && parsed.length > 1) {
                    setMultilabelDelimiter('list');
                    return;
                }
            } catch (e) {
                // ignore
            }

            // Priority 2: Comma (,)
            if (textToAnalyze.includes(',')) {
                const parts = textToAnalyze.split(',').filter(Boolean);
                if (parts.length > 1) {
                    setMultilabelDelimiter(',');
                    return;
                }
            }

            // Priority 3: Semicolon (;)
            if (textToAnalyze.includes(';')) {
                const parts = textToAnalyze.split(';').filter(Boolean);
                if (parts.length > 1) {
                    setMultilabelDelimiter(';');
                    return;
                }
            }

            // Priority 4: Pipe (|)
            if (textToAnalyze.includes('|')) {
                const parts = textToAnalyze.split('|').filter(Boolean);
                if (parts.length > 1) {
                    setMultilabelDelimiter('|');
                    return;
                }
            }

            // Priority 5: Colon (:)
            if (textToAnalyze.includes(':')) {
                const parts = textToAnalyze.split(':').filter(Boolean);
                if (parts.length > 1) {
                    setMultilabelDelimiter(':');
                    return;
                }
            }

            // Priority 6: Space
            if (textToAnalyze.includes(' ')) {
                const parts = textToAnalyze.split(' ').filter(Boolean);
                if (parts.length > 1) {
                    setMultilabelDelimiter(' ');
                    return;
                }
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rawResponse, responseType, taskType, isMultilabel, parsedJson, normalizedJsonLabelField]);

    useEffect(() => {
        if (taskType === TaskType.classification && responseType === 'text' && initialParserSelections?.classification) {
            const { delimiter } = initialParserSelections.classification;
            if (delimiter && delimiter !== multilabelDelimiter) {
                setMultilabelDelimiter(delimiter);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [taskType, responseType, initialParserSelections]);

    // A prefill only suppresses the "highlight text" hint when its field path
    // actually exists in the current JSON — a stale path (e.g. the output's
    // keys changed since the parser was saved) selects nothing, so the user
    // still needs the hint.
    const initialClassificationPath = initialParserSelections?.classification?.fieldPath;
    const initialSummarizationPath = initialParserSelections?.summarization?.fieldPath;
    const hasInitialClassificationPrefill = !!initialClassificationPath && !!jsonFieldMetaMap[initialClassificationPath];
    const hasInitialSummarizationPrefill = !!initialSummarizationPath && !!jsonFieldMetaMap[initialSummarizationPath];
    // Content generation defaults to "entire response", so once an output
    // exists there is nothing the user must highlight — only the pre-run
    // "Click Run" hint remains relevant.
    const showHighlightInstruction = responseType === 'json'
        && (taskType === TaskType.classification || isGenerativeTask(taskType))
        && (taskType !== TaskType.contentGeneration || !rawResponse)
        && Object.keys(selectedJsonTokens).length === 0
        && !hasInitialClassificationPrefill
        && !hasInitialSummarizationPrefill;

    // Whether the current raw response parses as JSON. Drives both the
    // auto-detect effect below and whether the "JSON" option is offered in
    // the Response type dropdown. Empty output is treated as "not JSON" for
    // auto-detect, but the dropdown still offers JSON when there's no output
    // yet (setup before running).
    const isRawResponseValidJson = useMemo(
        () => isParseableJsonStructure(rawResponse),
        [rawResponse]
    );

    // Remembers the rawResponse for which the user manually picked a response
    // type, so auto-detect doesn't immediately override that choice (e.g.
    // picking "Text" on a valid-JSON output). A new output re-enables
    // auto-detect.
    const manualResponseTypeForRawResponseRef = useRef(null);

    // Auto-detect response type: switch between 'json' and 'text' based on rawResponse content
    useEffect(() => {
        if (!rawResponse || !rawResponse.trim()) {
            return;
        }

        // Don't auto-detect for NER tasks (they don't support text mode)
        if (taskType === TaskType.NER || taskType === TaskType.NER_WITH_RELATIONS) {
            return;
        }

        // Don't auto-detect if user has selected 'custom' mode
        if (responseType === 'custom') {
            return;
        }

        // Don't override a manual choice made for this same output
        if (manualResponseTypeForRawResponseRef.current === rawResponse) {
            return;
        }

        // Auto-switch based on detection
        if (isRawResponseValidJson && responseType === 'text') {
            // Valid JSON detected but currently on 'text', switch to 'json'
            setResponseType('json');
        } else if (!isRawResponseValidJson && responseType === 'json') {
            // Not valid JSON but currently on 'json', switch to 'text'
            setResponseType('text');
        }
    }, [rawResponse, responseType, taskType, setResponseType, isRawResponseValidJson]);

    // Whether the "JSON" option should appear in the Response type dropdown.
    // Hidden when the output exists but isn't valid JSON (picking it would be
    // a dead end — the auto-detect effect would snap back to "Text"). Shown
    // while there's no output yet (setup before running). NER tasks are
    // exempt: they have no text mode and expect JSON.
    const showJsonResponseTypeOption =
        taskType === TaskType.NER ||
        taskType === TaskType.NER_WITH_RELATIONS ||
        !rawResponse || !rawResponse.trim() ||
        isRawResponseValidJson;

    const handleResponseTypeChange = useCallback((nextType) => {
        manualResponseTypeForRawResponseRef.current = rawResponse;
        setResponseType(nextType);
    }, [rawResponse, setResponseType]);

    useEffect(() => {
        setSelectedJsonTokens({});
    }, [responseType]);

    useEffect(() => {
        setSelectedJsonTokens({});
    }, [taskType]);

    useEffect(() => {
        setNerFieldAssignments(getEmptyNerAssignments());
    }, [responseType, taskType, rawResponse]);

    const getJsonValueByPath = useCallback((value, path) => {
        if (!value || !path) {
            return undefined;
        }
        const normalizedPath = path
            .replace(/\[(\d+)\]/g, '.$1')
            .replace(/^\./, '');
        if (!normalizedPath) {
            return value;
        }
        return normalizedPath
            .split('.')
            .filter(Boolean)
            .reduce((acc, segment) => (
                acc === undefined || acc === null ? acc : acc[segment]
            ), value);
    }, []);

    // True when the single-label selection points at a list-valued field, i.e.
    // the "first item vs entire list" choice applies.
    const selectedSingleLabelFieldIsList = useMemo(() => (
        taskType === TaskType.classification
        && !isMultilabel
        && responseType === 'json'
        && !!parsedJson
        && !!normalizedJsonLabelField
        && Array.isArray(getJsonValueByPath(parsedJson, normalizedJsonLabelField))
    ), [taskType, isMultilabel, responseType, parsedJson, normalizedJsonLabelField, getJsonValueByPath]);

    const collectMatchingFieldTokens = useCallback((targetPath) => {
        if (!parsedJson || !targetPath) {
            return [];
        }
        const matches = [];
        const traverse = (node, pathKey, fieldPath) => {
            const normalized = normalizeFieldPath(fieldPath || '');
            if (normalized === targetPath && (node === null || typeof node !== 'object')) {
                matches.push({
                    pathKey,
                    displayValue: formatPrimitive(node),
                });
            }
            if (Array.isArray(node)) {
                node.forEach((item, index) => {
                    const nextPathKey = `${pathKey}.${index}`;
                    const nextFieldPath = fieldPath ? `${fieldPath}[${index}]` : `[${index}]`;
                    traverse(item, nextPathKey, nextFieldPath);
                });
            } else if (node && typeof node === 'object') {
                Object.entries(node).forEach(([key, child]) => {
                    const nextPathKey = `${pathKey}.${key}`;
                    const nextFieldPath = fieldPath ? `${fieldPath}.${key}` : key;
                    traverse(child, nextPathKey, nextFieldPath);
                });
            }
        };
        traverse(parsedJson, 'root', '');
        return matches;
    }, [parsedJson]);

    // Tokens to highlight for a selected field, aligned with the Label value
    // pill (and with what the generated parser returns). Single-label on a
    // list-valued field: 'first' highlights only the first item; 'full' puts
    // one token on the array node itself so the tree shows the whole list —
    // brackets included — as the selected tag. Everything else keeps every
    // primitive match.
    const buildTokensForField = useCallback((fieldPath) => {
        if (!fieldPath) return {};
        if (taskType === TaskType.classification && !isMultilabel) {
            const fieldValue = getJsonValueByPath(parsedJson, fieldPath);
            if (Array.isArray(fieldValue)) {
                if (singleLabelListMode === 'full') {
                    return { [`root.${fieldPath}`]: formatNerAssignedValue(fieldValue) };
                }
                const [first] = collectMatchingFieldTokens(fieldPath);
                return first ? { [first.pathKey]: first.displayValue } : {};
            }
        }
        const tokens = {};
        collectMatchingFieldTokens(fieldPath).forEach(({ pathKey, displayValue }) => {
            tokens[pathKey] = displayValue;
        });
        return tokens;
        // ``formatNerAssignedValue`` is a pure per-render function; listing it
        // would only churn the callback identity.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [taskType, isMultilabel, singleLabelListMode, parsedJson, getJsonValueByPath, collectMatchingFieldTokens]);

    const classificationPrefill = useMemo(() => {
        if (taskType !== TaskType.classification) {
            return null;
        }
        if (responseType === 'text') {
            return {
                type: 'text',
                fieldPath: null,
                isMultilabel,
                delimiter: isMultilabel ? multilabelDelimiter : null,
            };
        }
        if (responseType !== 'json') {
            return null;
        }
        if (!normalizedJsonLabelField || Object.keys(selectedJsonTokens).length === 0) {
            return null;
        }
        const fieldMeta = jsonFieldMetaMap[normalizedJsonLabelField] || {};
        return {
            type: isMultilabel ? 'multi' : 'single',
            fieldPath: normalizedJsonLabelField,
            arrayPath: fieldMeta.arrayPath ?? null,
            delimiter: isMultilabel ? multilabelDelimiter : null,
            // Only meaningful for single-label selections on list-valued
            // fields — drives the first-item vs entire-list parser code.
            listMode: !isMultilabel && selectedSingleLabelFieldIsList ? singleLabelListMode : null,
        };
    }, [taskType, responseType, normalizedJsonLabelField, selectedJsonTokens, isMultilabel, jsonFieldMetaMap, multilabelDelimiter, selectedSingleLabelFieldIsList, singleLabelListMode]);

    const nerEntityPrefill = useMemo(() => {
        const labelPath = nerFieldAssignments.label?.path;
        const contentPath = nerFieldAssignments.content?.path;
        const startIndexPath = nerFieldAssignments.startIndex?.path;
        if (!labelPath || !contentPath || !startIndexPath) {
            return null;
        }
        return {
            labelPath,
            labelArrayPath: nerFieldAssignments.label?.arrayPath ?? null,
            contentPath,
            contentArrayPath: nerFieldAssignments.content?.arrayPath ?? null,
            startIndexPath,
            startIndexArrayPath: nerFieldAssignments.startIndex?.arrayPath ?? null,
        };
    }, [nerFieldAssignments]);

    const nerRelationsPrefill = useMemo(() => {
        if (!supportsRelationFields) {
            return null;
        }
        const labelPath = nerFieldAssignments.relationLabel?.path;
        const sourceIndexPath = nerFieldAssignments.relationSourceIndex?.path;
        const targetIndexPath = nerFieldAssignments.relationTargetIndex?.path;
        if (!labelPath || !sourceIndexPath || !targetIndexPath) {
            return null;
        }
        return {
            labelPath,
            labelArrayPath: nerFieldAssignments.relationLabel?.arrayPath ?? null,
            sourceIndexPath,
            sourceArrayPath: nerFieldAssignments.relationSourceIndex?.arrayPath ?? null,
            targetIndexPath,
            targetArrayPath: nerFieldAssignments.relationTargetIndex?.arrayPath ?? null,
        };
    }, [nerFieldAssignments, supportsRelationFields]);

    const summarizationPrefill = useMemo(() => {
        if (!isGenerativeTask(taskType)) {
            return null;
        }
        if (responseType === 'text') {
            return {
                type: 'text',
                fieldPath: null,
            };
        }
        if (responseType !== 'json') {
            return null;
        }
        // Content generation: mapping is optional — the explicit field pick
        // alone decides (token matches are not required, e.g. object-valued
        // fields), and no pick means the entire returned object.
        const hasFieldSelection = taskType === TaskType.contentGeneration
            ? !!normalizedJsonLabelField
            : normalizedJsonLabelField && Object.keys(selectedJsonTokens).length > 0;
        if (taskType === TaskType.contentGeneration && !hasFieldSelection) {
            return {
                type: 'json',
                fieldPath: null,
                arrayPath: null,
            };
        }
        if (!hasFieldSelection) {
            return null;
        }
        const fieldMeta = jsonFieldMetaMap[normalizedJsonLabelField] || {};
        return {
            type: 'json',
            fieldPath: normalizedJsonLabelField,
            arrayPath: fieldMeta.arrayPath ?? null,
        };
    }, [taskType, responseType, normalizedJsonLabelField, selectedJsonTokens, jsonFieldMetaMap]);

    useEffect(() => {
        notifyParserPrefill({
            classification: classificationPrefill,
            ner: nerEntityPrefill,
            relations: nerRelationsPrefill,
            summarization: summarizationPrefill,
        });
    }, [classificationPrefill, nerEntityPrefill, nerRelationsPrefill, summarizationPrefill, notifyParserPrefill]);

    // Notify parent about code update when prefill changes
    useEffect(() => {
        let snippet = null;
        if (taskType === TaskType.classification && classificationPrefill) {
            snippet = generateInitialParserCode(taskType, { classification: classificationPrefill });
        } else if (taskType === TaskType.NER && nerEntityPrefill) {
            snippet = generateInitialParserCode(taskType, { ner: nerEntityPrefill });
        } else if (taskType === TaskType.NER_WITH_RELATIONS && nerEntityPrefill && nerRelationsPrefill) {
            snippet = generateInitialParserCode(taskType, { ner: nerEntityPrefill, relations: nerRelationsPrefill });
        } else if (isGenerativeTask(taskType) && summarizationPrefill) {
            snippet = generateInitialParserCode(taskType, { summarization: summarizationPrefill });
        }

        if (snippet && onUserCodeChange) {
            onUserCodeChange(snippet, { fromAuto: true });
        }
    }, [classificationPrefill, nerEntityPrefill, nerRelationsPrefill, summarizationPrefill, taskType, onUserCodeChange]);

    const hasAutoSelectedRef = useRef(false);
    // True after the user clears or picks a JSON field. The auto-select effect
    // must not snap back to the initial/default field (e.g. "result") on that
    // click, or when a later parser-save round-trip re-supplies that initial path.
    const userControlledJsonSelectionRef = useRef(false);
    // Same idea for the single-label list mode: a manual toggle must not be
    // overridden by a stale saved-parser round-trip in the same session.
    const userControlledListModeRef = useRef(false);

    useEffect(() => {
        hasAutoSelectedRef.current = false;
        userControlledJsonSelectionRef.current = false;
        userControlledListModeRef.current = false;
        setSingleLabelListMode('first');
    }, [rawResponse]);

    useEffect(() => {
        // Changing response type or task type should allow auto-selection again.
        hasAutoSelectedRef.current = false;
        userControlledJsonSelectionRef.current = false;
        userControlledListModeRef.current = false;
        setSingleLabelListMode('first');
    }, [responseType, taskType]);

    // Restore the saved single-label list mode when a parser round-trips back
    // through initialParserSelections (old whole-list parsers derive 'full',
    // first-item parsers derive 'first').
    useEffect(() => {
        if (userControlledListModeRef.current) return;
        const initialMode = initialParserSelections?.classification?.listMode;
        if (initialMode === 'first' || initialMode === 'full') {
            setSingleLabelListMode(initialMode);
        }
        // Mirrors the auto-select effect: re-apply when output or mode context
        // changes, not on every selection click.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialParserSelections, parsedJson, responseType, taskType]);

    const handleSingleLabelListModeChange = useCallback((mode) => {
        if (mode !== 'first' && mode !== 'full') return;
        userControlledListModeRef.current = true;
        setSingleLabelListMode(mode);
    }, []);

    // Toggling first/full re-derives the highlighted tokens for the current
    // field so the tree highlights exactly what the pill shows: the first
    // list item, or the whole array node as one tag.
    useEffect(() => {
        if (taskType !== TaskType.classification || isMultilabel) return;
        if (responseType !== 'json' || !parsedJson || !normalizedJsonLabelField) return;
        if (Object.keys(selectedJsonTokens).length === 0) return;
        const nextTokens = buildTokensForField(normalizedJsonLabelField);
        if (!Object.keys(nextTokens).length) return;
        if (JSON.stringify(nextTokens) !== JSON.stringify(selectedJsonTokens)) {
            setSelectedJsonTokens(nextTokens);
        }
        // Only the mode toggle should trigger this sync — the click handlers
        // already build mode-aware tokens for every other path.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [singleLabelListMode]);

    const defaultLabelField = useMemo(() => {
        if (!jsonLabelFieldOptions.length) return null;
        return jsonLabelFieldOptions[0].value;
    }, [jsonLabelFieldOptions]);

    useEffect(() => {
        if (responseType !== 'json') return;
        if (!parsedJson) return;
        if (userControlledJsonSelectionRef.current) return;

        // Handle classification initial selections
        if (taskType === TaskType.classification) {
            const initialInfo = initialParserSelections?.classification;
            const initialPath = initialInfo?.fieldPath;
            const initialDelimiter = initialInfo?.delimiter;

            // 1. Apply Initial Selections (Priority) - Only runs when initialParserSelections actually changes
            if (initialPath) {
                // Update Label Field if needed
                if (selectedJsonLabelField !== initialPath) {
                    setSelectedJsonLabelField(initialPath);
                }

                // Update Delimiter if needed
                if (isMultilabel && initialDelimiter && multilabelDelimiter !== initialDelimiter) {
                    setMultilabelDelimiter(initialDelimiter);
                }

                // Ensure tokens are selected for this path
                const nextTokens = buildTokensForField(initialPath);
                if (Object.keys(nextTokens).length) {
                    const prevKeys = Object.keys(selectedJsonTokens).sort();
                    const nextKeys = Object.keys(nextTokens).sort();
                    if (JSON.stringify(prevKeys) !== JSON.stringify(nextKeys)) {
                        setSelectedJsonTokens(nextTokens);
                    }
                }

                hasAutoSelectedRef.current = true;
                return;
            }

            // 2. Default Fallback (Only if no initial selection AND no user selection yet)
            if (Object.keys(selectedJsonTokens).length > 0) return;
            if (hasAutoSelectedRef.current) return;

            // Fallback to default normalized label field if no initial selection from code
            if (!initialPath && defaultLabelField) {
                const fieldPath = defaultLabelField;
                const nextTokens = buildTokensForField(fieldPath);
                if (!Object.keys(nextTokens).length) {
                    return;
                }
                setSelectedJsonTokens(nextTokens);

                if (selectedJsonLabelField !== fieldPath) {
                    setSelectedJsonLabelField(fieldPath);
                }

                hasAutoSelectedRef.current = true;
            }
        }

        // Handle summarization initial selections
        if (isGenerativeTask(taskType)) {
            const initialInfo = initialParserSelections?.summarization;
            const initialPath = initialInfo?.fieldPath;

            // 1. Apply Initial Selections (Priority)
            if (initialPath) {
                if (selectedJsonLabelField !== initialPath) {
                    setSelectedJsonLabelField(initialPath);
                }

                const matches = collectMatchingFieldTokens(initialPath);
                if (matches.length) {
                    const nextTokens = {};
                    matches.forEach(({ pathKey, displayValue }) => {
                        nextTokens[pathKey] = displayValue;
                    });

                    const prevKeys = Object.keys(selectedJsonTokens).sort();
                    const nextKeys = Object.keys(nextTokens).sort();
                    if (JSON.stringify(prevKeys) !== JSON.stringify(nextKeys)) {
                        setSelectedJsonTokens(nextTokens);
                    }
                }

                hasAutoSelectedRef.current = true;
                return;
            }

            // Content generation: no default field selection — "entire
            // response" is the default, mapping a field is optional.
            if (taskType === TaskType.contentGeneration) return;

            // 2. Default Fallback (Only if no initial selection AND no user selection yet)
            if (Object.keys(selectedJsonTokens).length > 0) return;
            if (hasAutoSelectedRef.current) return;

            if (!initialPath && defaultLabelField) {
                const fieldPath = defaultLabelField;
                const matches = collectMatchingFieldTokens(fieldPath);
                if (!matches.length) {
                    return;
                }
                const nextTokens = {};
                matches.forEach(({ pathKey, displayValue }) => {
                    nextTokens[pathKey] = displayValue;
                });
                setSelectedJsonTokens(nextTokens);

                if (selectedJsonLabelField !== fieldPath) {
                    setSelectedJsonLabelField(fieldPath);
                }

                hasAutoSelectedRef.current = true;
            }
        }
        // Do not depend on selectedJsonTokens / selectedJsonLabelField — those
        // change on every user click and must not re-apply the initial path.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialParserSelections, parsedJson, responseType, taskType, isMultilabel, collectMatchingFieldTokens, defaultLabelField]);

    const handleLabelFieldChange = (value) => {
        userControlledJsonSelectionRef.current = true;
        setSelectedJsonLabelField(value);
        // Force update tokens immediately when dropdown changes
        setSelectedJsonTokens(buildTokensForField(value));
    };

    useEffect(() => {
        if (!initialParserSelections) {
            return;
        }
        if (!parsedJson) {
            return;
        }
        setNerFieldAssignments((prev) => {
            const next = { ...prev };
            let changed = false;
            const assign = (key, path, arrayPath) => {
                if (!path || next[key]?.path) {
                    return;
                }
                const value = formatNerAssignedValue(getFirstValueForPath(parsedJson, path));
                next[key] = {
                    path,
                    label: getLabelFromFieldPath(path),
                    value,
                    arrayPath: arrayPath ?? jsonFieldMetaMap[path]?.arrayPath ?? null,
                };
                changed = true;
            };
            if (initialParserSelections.ner) {
                assign('label', initialParserSelections.ner.labelPath, initialParserSelections.ner.labelArrayPath);
                assign('content', initialParserSelections.ner.contentPath, initialParserSelections.ner.contentArrayPath);
                assign('startIndex', initialParserSelections.ner.startIndexPath, initialParserSelections.ner.startIndexArrayPath);
            }
            if (supportsRelationFields && initialParserSelections.relations) {
                assign('relationLabel', initialParserSelections.relations.labelPath, initialParserSelections.relations.labelArrayPath);
                assign('relationSourceIndex', initialParserSelections.relations.sourceIndexPath, initialParserSelections.relations.sourceArrayPath);
                assign('relationTargetIndex', initialParserSelections.relations.targetIndexPath, initialParserSelections.relations.targetArrayPath);
            }
            return changed ? next : prev;
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialParserSelections, parsedJson, supportsRelationFields, jsonFieldMetaMap]);

    const toggleJsonSelection = (path, valueDisplay, fieldPath) => {
        if ((taskType === TaskType.classification || isGenerativeTask(taskType)) && fieldPath) {
            // If the clicked token is already selected, unselect all (clear selection)
            if (selectedJsonTokens[path]) {
                userControlledJsonSelectionRef.current = true;
                setSelectedJsonTokens({});
                setSelectedJsonLabelField(null);
                return;
            }

            userControlledJsonSelectionRef.current = true;
            const normalizedPath = normalizeFieldPath(fieldPath);
            setSelectedJsonLabelField(normalizedPath);

            // Force update tokens immediately to keep UI in sync without relying on effect
            const nextTokens = buildTokensForField(normalizedPath);
            if (Object.keys(nextTokens).length) {
                setSelectedJsonTokens(nextTokens);
            }
            return;
        }

        setSelectedJsonTokens((prev) => {
            const alreadySelected = !!prev[path];
            if (alreadySelected) {
                userControlledJsonSelectionRef.current = true;
                const { [path]: _, ...rest } = prev;
                return rest;
            }

            userControlledJsonSelectionRef.current = true;
            const nextSelection = {
                ...(taskType === TaskType.classification || isGenerativeTask(taskType) ? {} : prev),
                [path]: valueDisplay,
            };

            if (fieldPath && (taskType === TaskType.classification || isGenerativeTask(taskType))) {
                setSelectedJsonLabelField(fieldPath);
            }

            return nextSelection;
        });
    };

    const getLabelFromFieldPath = (fieldPath) => {
        if (!fieldPath) return '';
        const segments = fieldPath.split('.');
        const lastSegment = segments[segments.length - 1] || fieldPath;
        const withoutIndexes = lastSegment.replace(/\[(\d+)\]/g, '');
        if (withoutIndexes) {
            return withoutIndexes;
        }
        const indexOnly = lastSegment.replace(/\[(\d+)\]/g, '$1');
        return indexOnly || lastSegment;
    };

    const handleNerFieldAssignment = (fieldKey, meta) => {
        if (!meta.fieldPath) {
            return;
        }
        const normalizedPath = normalizeFieldPath(meta.fieldPath);
        const metaInfo = jsonFieldMetaMap[normalizedPath] || {};
        const resolvedLabel = meta.label || getLabelFromFieldPath(meta.fieldPath);
        const resolvedValue = formatNerAssignedValue(meta.value);
        setNerFieldAssignments((prev) => {
            const next = { ...prev };

            // Prevent the same JSON path from being assigned to multiple fields at once.
            // If another field is already using this path, clear that older assignment.
            Object.keys(next).forEach((key) => {
                if (key !== fieldKey && next[key]?.path === normalizedPath) {
                    next[key] = null;
                }
            });

            next[fieldKey] = {
                path: normalizedPath,
                label: resolvedLabel,
                value: resolvedValue,
                arrayPath: meta.arrayPath ?? metaInfo.arrayPath ?? null,
            };

            return next;
        });
    };

    const handleManualNerAssignment = (fieldKey, fieldPath) => {
        if (!fieldPath) {
            handleRemoveNerAssignment(fieldKey);
            return;
        }
        const normalizedPath = normalizeFieldPath(fieldPath);
        const resolvedValue = formatNerAssignedValue(getFirstValueForPath(parsedJson, normalizedPath));
        const metaInfo = jsonFieldMetaMap[normalizedPath] || {};
        handleNerFieldAssignment(fieldKey, {
            fieldPath: normalizedPath,
            label: getLabelFromFieldPath(normalizedPath),
            value: resolvedValue,
            arrayPath: metaInfo.arrayPath ?? null,
        });
    };

    const handleRemoveNerAssignment = (fieldKey) => {
        setNerFieldAssignments((prev) => ({
            ...prev,
            [fieldKey]: null,
        }));
    };

    const buildNerDropdownMenu = (meta) => ({
        items: nerFieldMenuItems,
        onClick: ({ key, domEvent }) => {
            domEvent?.stopPropagation();
            handleNerFieldAssignment(key, meta);
        },
    });

    const splitLabels = (value) => {
        if (!value) return [];

        if (multilabelDelimiter === 'list') {
            try {
                const parsed = JSON.parse(value);
                if (Array.isArray(parsed)) {
                    return parsed.map(item => String(item).trim());
                }
            } catch (e) {
                try {
                    // Replace single quotes with double quotes for Python-style lists
                    const jsonStr = value.replace(/'/g, '"');
                    const parsed = JSON.parse(jsonStr);
                    if (Array.isArray(parsed)) {
                        return parsed.map(item => String(item).trim());
                    }
                } catch (e2) {
                    // parsing failed, treat as single string
                }
            }
            return [value];
        }

        return value
            .split(multilabelDelimiter || ',')
            .map(item => item.trim())
            .filter(Boolean);
    };

    return {
        // Parsed raw response
        parsedJson,
        jsonParseError,
        // Response type
        handleResponseTypeChange,
        showJsonResponseTypeOption,
        showHighlightInstruction,
        // JSON viewer
        isNerJsonMode,
        selectedJsonTokens,
        nerPathAssignments,
        toggleJsonSelection,
        handleRemoveNerAssignment,
        buildNerDropdownMenu,
        jsonFieldMetaMap,
        getLabelFromFieldPath,
        // Classification / summarization summaries
        normalizedJsonLabelField,
        jsonLabelFieldOptions,
        handleLabelFieldChange,
        multilabelDelimiter,
        setMultilabelDelimiter,
        singleLabelListMode,
        setSingleLabelListMode: handleSingleLabelListModeChange,
        selectedSingleLabelFieldIsList,
        getJsonValueByPath,
        formatNerAssignedValue,
        splitLabels,
        delimiterOptions,
        // NER summary
        handleManualNerAssignment,
        jsonFieldSelectOptions,
        entityFieldConfig,
        relationFieldConfig,
        supportsRelationFields,
        nerFieldAssignments,
    };
};

export default useResponseMappingState;
